/**
 * sectionDataKeys — the SINGLE SHARED id → report-data-key map for the
 * export/projection surfaces.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * A report SECTION id (the registry/section-order key, e.g. `groundwater`,
 * `bop_economics`) frequently DIFFERS from the field on `SiteRagReport` that
 * carries the data (`waterAnalysis.groundwaterAssessment`, `bopEconomics`).
 * Before this map the PPTX projection (`reportPptxSections.ts`) looked the data
 * up by section id (`obj[id]`) and SILENTLY dropped the whole body of every
 * section whose id ≠ data key — a customer downloaded a near-empty deck while
 * the on-screen view was complete (the Wave-1 PPTX whole-body data-loss).
 *
 * This module makes the id→key correspondence EXPLICIT and centrally owned so
 * the drift can never silently recur: a new section whose id ≠ data key MUST
 * add an entry here (or the projection's switch must map it), and that is a
 * visible diff in review.
 *
 * ── SCOPE ───────────────────────────────────────────────────────────────────
 * The mapped entries are the STRUCTURED-object / scalar typed-analysis sections
 * whose data lives under a typed-analysis container or a renamed scalar field.
 * Each entry resolves to either:
 *   - a single dotted PATH (string) on the report (`'waterAnalysis.groundwaterAssessment'`),
 *   - an ARRAY of fallback paths tried in order (first non-empty wins), or
 *   - a structured `{ container, fields }` descriptor for an object section
 *     whose populated named fields each become one label-prefixed bullet.
 *
 * The `getByPath` helper resolves a dotted path null-safely. The DOCX exporter
 * (`reportDocxExport.ts`) currently hard-codes the SAME correspondences in its
 * `registryTypedEmitters`; it can adopt this map later (no DOCX refactor in
 * Wave 1) — this map was authored by mirroring those emitters' data sourcing
 * field-for-field so the two surfaces stay in lockstep.
 */

/** Null-safe dotted-path read (`getByPath(report, 'waterAnalysis.groundwaterAssessment')`). */
export function getByPath(obj: unknown, path: string): unknown {
  if (obj == null) return undefined;
  let cur: unknown = obj;
  for (const key of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

/** First non-empty string resolved from an ordered list of dotted paths. */
export function firstStringByPath(obj: unknown, paths: readonly string[]): string | undefined {
  for (const p of paths) {
    const v = getByPath(obj, p);
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

/**
 * An object-section descriptor: the data lives on `container`, and each entry of
 * `fields` (`[dataKey, humanLabel]`) becomes one `Label: value` bullet when the
 * field is a populated string. `fields` ORDER is the bullet order (mirrors the
 * DOCX sub-heading order for the section).
 */
export interface ObjectSectionDescriptor {
  /** Dotted path to the container object on the report (`'waterResources'`, `'powerAnalysis'`). */
  container: string;
  /** Ordered `[dataKey, humanLabel]` pairs — each populated string field → one bullet. */
  fields: ReadonlyArray<readonly [dataKey: string, label: string]>;
}

/**
 * SCALAR section data-key map: section id → ordered fallback paths on the report.
 * The projection bullets the FIRST non-empty string the paths resolve. Mirrors
 * the DOCX emitters' data sourcing exactly (see reportDocxExport.ts).
 */
export const SECTION_SCALAR_DATA_KEY: Record<string, readonly string[]> = {
  waterScreening: ['waterScreening'],
  // ── water-resource (report.waterAnalysis.*) ──────────────────────────────
  waterRights: ['waterAnalysis.waterRightsAnalysis'],
  coolingByTech: ['waterAnalysis.coolingWaterRequirements'],
  conservation: ['waterAnalysis.waterConservation'],
  wastewaterStormwater: ['waterAnalysis.wastewaterManagement'],
  // ── power-availability (report.powerAnalysis.*) ──────────────────────────
  rateStructures: ['powerAnalysis.rateStructure'],
  gridRedundancy: ['powerAnalysis.redundancyAssessment'],
  // ── test-fit (report.testFitAnalysis.* + renamed scalars) ────────────────
  parcelOverview: ['testFitAnalysis.siteConstraints'],
  phasedBuildout: ['testFitAnalysis.phaseLayout'],
  bop_economics: ['bopEconomics'], // scalar id≠key: section `bop_economics` ← `report.bopEconomics`
  // ── interconnection (report.interconnectionAnalysis.*) ───────────────────
  fiberRings: ['interconnectionAnalysis.fiberAvailability'],
  latencyModel: ['interconnectionAnalysis.latencyModeling'],
};

/**
 * OBJECT section descriptors: section id → { container, fields }. Each populated
 * string field becomes one `Label: value` bullet. Mirrors the DOCX object
 * emitters' sub-heading order field-for-field.
 */
export const SECTION_OBJECT_DATA_KEY: Record<string, ObjectSectionDescriptor> = {
  // ── power-availability — grid capacity folds substation + capacity study.
  // `SECTION_FIELD_PATHS` maps this section to `powerAnalysis.substationAssessment`
  // ALONE; the DOCX emitter renders both sub-headings. Kept here so the second
  // field cannot be silently dropped from the document model.
  gridCapacity: {
    container: 'powerAnalysis',
    fields: [
      ['substationAssessment', 'Substation Assessment'],
      ['capacityAnalysis', 'Capacity Analysis'],
    ],
  },
  // ── water-resource — groundwater section folds BOTH ground + surface water.
  groundwater: {
    container: 'waterAnalysis',
    fields: [
      ['groundwaterAssessment', 'Groundwater Assessment'],
      ['surfaceWaterAssessment', 'Surface Water'],
    ],
  },
  // ── environmental structured analysis objects (each a top-level report field).
  ecologicalAssessment: {
    container: 'ecologicalAssessment',
    fields: [
      ['criticalHabitatAnalysis', 'Critical Habitat'],
      ['esaSpeciesAssessment', 'ESA-Listed Species'],
      ['wetlandsDelineation', 'Wetlands Delineation'],
      ['wildlifeCorridors', 'Wildlife Corridors'],
      ['section7Requirements', 'ESA Section 7'],
      ['section404Requirements', 'CWA Section 404'],
    ],
  },
  waterResources: {
    container: 'waterResources',
    fields: [
      ['waterStressAnalysis', 'Water Stress'],
      ['waterRightsAssessment', 'Water Rights'],
      ['groundwaterSurfaceWater', 'Groundwater & Surface Water'],
      ['thermalDischarge', 'Thermal Discharge (316(a))'],
      ['stormwaterManagement', 'Stormwater Management'],
    ],
  },
  airQualityEmissions: {
    container: 'airQualityEmissions',
    fields: [
      ['baselineAqi', 'Baseline AQI'],
      ['attainmentStatus', 'Attainment Status'],
      ['criteriaPollutants', 'Criteria Pollutants'],
      ['ghgImplications', 'GHG Implications'],
      ['constructionEmissions', 'Construction Emissions'],
    ],
  },
  floodNaturalHazards: {
    container: 'floodNaturalHazards',
    fields: [
      ['femaFloodAnalysis', 'FEMA Flood Analysis'],
      ['coastalZoneManagement', 'Coastal Zone Management'],
      ['seaLevelRise', 'Sea Level Rise'],
      ['seismicImpact', 'Seismic Impact'],
    ],
  },
  culturalHistorical: {
    container: 'culturalHistorical',
    fields: [
      ['tribalConsultation', 'Tribal Consultation (NHPA §106)'],
      ['archaeologicalSensitivity', 'Archaeological Sensitivity'],
    ],
  },
  landUseZoning: {
    container: 'landUseZoning',
    fields: [
      ['currentZoning', 'Current Zoning'],
      ['compatibilityAnalysis', 'Compatibility'],
    ],
  },
  noiseVisualImpact: {
    container: 'noiseVisualImpact',
    fields: [
      ['constructionNoise', 'Construction Noise'],
      ['operationalNoise', 'Operational Noise'],
    ],
  },
};
